import ResizePlugin from "./ResizePlugin";
import IframeOnClick from "./IframeClick";
import type { Locale } from "./i18n";

interface Quill {
    container: HTMLElement;
    root: HTMLElement; // edit area
    on: any;
    updateContents: (delta: any) => void;
    getContents: () => any;
    isEnabled: () => boolean;
}

interface QuillResizeImageOptions {
    [index: string]: any;
    locale?: Locale;
    disableMediaTypes?: {
        disableImages?: boolean;
        disableVideos?: boolean;
        disableIframes?: boolean;
    };
    keepAspectRatio?: boolean;
    viewOnly?: boolean;
    onImageClick?: (img: HTMLImageElement) => void;
    onResizeEnd?: (img: HTMLImageElement) => void;
    resizeConstraints?: {
        minWidth?: number;
        maxWidth?: number;
        minHeight?: number;
        maxHeight?: number;
    };
}

function QuillResizeImage(quill: Quill, options?: QuillResizeImageOptions) {
    const container: HTMLElement = quill.root as HTMLElement;
    let resizeTarge: HTMLElement | null = null;
    let resizePlugin: ResizePlugin | null = null;

    // View-only mode (e.g. the shared read-only page): no hover resizer, no
    // resize handle, no settings gear. Images just open the fullscreen viewer
    // on click (a hand cursor is applied via CSS).
    if (options?.viewOnly) {
        container.addEventListener("click", (e: Event) => {
            const t = e.target as HTMLElement;
            if (t && t.tagName.toLowerCase() === "img" && options?.onImageClick) {
                options.onImageClick(t as HTMLImageElement);
            }
        });
        return;
    }

    function triggerTextChange() {
        const Delta = quill.getContents().constructor;
        const delta = new Delta().retain(1);
        quill.updateContents(delta);
    }

    function getMediaTarget(e: Event): HTMLElement | null {
        const t = e.target as HTMLElement;
        if (!t) return null;
        const tag = t.tagName.toLowerCase();
        if (tag === "img" && !options?.disableMediaTypes?.disableImages) return t;
        if (tag === "video" && !options?.disableMediaTypes?.disableVideos) return t;
        return null;
    }

    function attachPlugin(target: HTMLElement) {
        if (resizePlugin && resizeTarge === target) {
            resizePlugin.show();
            return;
        }
        if (resizePlugin) {
            resizePlugin.destory();
            resizePlugin = null;
            resizeTarge = null;
        }
        resizeTarge = target;
        resizePlugin = new ResizePlugin(
            target,
            container.parentElement as HTMLElement,
            container as HTMLElement,
            {
                ...options,
                readOnly: !quill.isEnabled(),
                onChange: triggerTextChange,
            }
        );
        resizePlugin.show();
    }

    // Show the resizer on hover; hide it shortly after the pointer leaves.
    container.addEventListener("mouseover", (e: Event) => {
        const target = getMediaTarget(e);
        if (target) attachPlugin(target);
    });

    container.addEventListener("mouseout", (e: MouseEvent) => {
        const target = getMediaTarget(e);
        if (target) {
            const related = e.relatedTarget as Node | null;
            if (related && resizePlugin?.resizer?.contains(related)) {
                return;
            }
            resizePlugin?.scheduleHide();
        }
    });

    quill.on("text-change", () => {
        // iframe 大小调整
        if (!options?.disableMediaTypes?.disableIframes && quill.isEnabled()) {
            container
                .querySelectorAll("iframe")
                .forEach((item: HTMLIFrameElement) => {
                    IframeOnClick.track(item, () => {
                        resizeTarge = item;
                        resizePlugin = new ResizePlugin(
                            item,
                            container.parentElement as HTMLElement,
                            container as HTMLElement,
                            {
                                ...options,
                                onChange: triggerTextChange,
                            }
                        );
                    });
                });
        }
    });

    document.addEventListener(
        "mousedown",
        (e: Event) => {
            const target = e.target as HTMLElement;
            if (
                target !== resizeTarge &&
                !resizePlugin?.resizer?.contains?.(target)
            ) {
                resizePlugin?.destory?.();
                resizePlugin = null;
                resizeTarge = null;
            }
        },
        {
            capture: true,
        }
    );
}

export default QuillResizeImage;
